import React, { useState } from 'react';
import { User, Users, Heart, Settings, ChevronRight } from 'lucide-react';
const Setup = () => {
  const [activeSection, setActiveSection] = useState<'profile' | 'connections' | 'settings'>('profile');
  const sections = {
    profile: {
      title: 'My Profile',
      description: 'Your personal information and preferences',
      icon: <User className="w-6 h-6 text-lavender" data-id="element-455" />,
      fields: [{
        label: 'Name',
        value: 'Maria',
        type: 'text'
      }, {
        label: 'Preferred Name',
        value: 'Mary',
        type: 'text'
      }, {
        label: 'Birthday',
        value: '1955-06-15',
        type: 'date'
      }, {
        label: 'Language',
        value: 'English',
        type: 'select'
      }]
    },
    connections: {
      title: 'Family & Friends',
      description: 'Connect with loved ones and caregivers',
      icon: <Users className="w-6 h-6 text-soft-coral" data-id="element-456" />,
      connections: [{
        name: 'Sarah',
        relation: 'Daughter',
        image: 'https://randomuser.me/api/portraits/women/44.jpg'
      }, {
        name: 'Michael',
        relation: 'Son',
        image: 'https://randomuser.me/api/portraits/men/32.jpg'
      }, {
        name: 'Dr. Thompson',
        relation: 'Caregiver',
        image: 'https://randomuser.me/api/portraits/men/52.jpg'
      }]
    },
    settings: {
      title: 'Preferences',
      description: 'Customize your experience',
      icon: <Settings className="w-6 h-6 text-mint-green" data-id="element-457" />,
      options: [{
        label: 'Text Size',
        value: 'Large'
      }, {
        label: 'Voice Speed',
        value: 'Normal'
      }, {
        label: 'Notifications',
        value: 'Important Only'
      }, {
        label: 'Theme',
        value: 'Light'
      }]
    }
  };
  return <div className="py-6 space-y-6" data-id="element-458">
      <h1 className="text-2xl font-bold dark:text-white" data-id="element-459">Setup</h1>
      {/* Main Sections */}
      <div className="space-y-4" data-id="element-460">
        {/* Profile Section */}
        <div className="card" data-id="element-461">
          <div className="flex items-start space-x-4" data-id="element-462">
            <div className="w-16 h-16 rounded-full bg-lavender/10 flex items-center justify-center" data-id="element-463">
              <User className="w-8 h-8 text-lavender" data-id="element-464" />
            </div>
            <div className="flex-1" data-id="element-465">
              <h2 className="text-xl font-medium dark:text-white mb-1" data-id="element-466">
                My Profile
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-467">
                Update your information and preferences
              </p>
              <div className="space-y-4" data-id="element-468">
                {sections.profile.fields.map(field => <div key={field.label} className="flex justify-between items-center" data-id="element-469">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-470">
                      {field.label}
                    </span>
                    <span className="font-medium" data-id="element-471">{field.value}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
        {/* Connections Section */}
        <div className="card" data-id="element-472">
          <div className="flex items-start space-x-4" data-id="element-473">
            <div className="w-16 h-16 rounded-full bg-soft-coral/10 flex items-center justify-center" data-id="element-474">
              <Users className="w-8 h-8 text-soft-coral" data-id="element-475" />
            </div>
            <div className="flex-1" data-id="element-476">
              <h2 className="text-xl font-medium dark:text-white mb-1" data-id="element-477">
                Family & Friends
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-478">
                Manage your connections
              </p>
              <div className="space-y-3" data-id="element-479">
                {sections.connections.connections.map(connection => <div key={connection.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-xl" data-id="element-480">
                    <div className="flex items-center space-x-3" data-id="element-481">
                      <img src={connection.image} alt={connection.name} className="w-10 h-10 rounded-full object-cover" data-id="element-482" />
                      <div data-id="element-483">
                        <div className="font-medium" data-id="element-484">{connection.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-485">
                          {connection.relation}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-486" />
                  </div>)}
              </div>
              <button className="mt-4 w-full p-3 bg-soft-coral/10 text-soft-coral rounded-xl hover:bg-soft-coral/20 transition-colors" data-id="element-487">
                Add New Connection
              </button>
            </div>
          </div>
        </div>
        {/* Settings Section */}
        <div className="card" data-id="element-488">
          <div className="flex items-start space-x-4" data-id="element-489">
            <div className="w-16 h-16 rounded-full bg-mint-green/10 flex items-center justify-center" data-id="element-490">
              <Settings className="w-8 h-8 text-mint-green" data-id="element-491" />
            </div>
            <div className="flex-1" data-id="element-492">
              <h2 className="text-xl font-medium dark:text-white mb-1" data-id="element-493">
                Preferences
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-494">
                Customize your experience
              </p>
              <div className="space-y-4" data-id="element-495">
                {sections.settings.options.map(option => <div key={option.label} className="flex justify-between items-center" data-id="element-496">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-497">
                      {option.label}
                    </span>
                    <span className="font-medium" data-id="element-498">{option.value}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Setup;